import 'reflect-metadata';

export enum PackageType {
    COMPONENT = 'component',
    MODULE = 'module',
    CUSTOM_CODES = 'custom_codes',
}

export enum Status {
    PROCESSING = 'processing',
    READY = 'ready',
    ERROR = 'error',
    IDLE = 'idle',
}

// Interface for component metadata
interface ComponentMetadata {
    category: string;
    label: string;
    name: string;
    iconPath: string;
    version: string;
    config: {
        paletteCategory: string;
        inputs: AttributeCategory[];
        outputs: any[];
        dependencyOptions: any[];
    };
}

interface PluginMetadata {
    packageId: string;
    versionId: string;
    packageName: string;
    packageLabel: string;
    version: string;
    exposedName: string;
}

// Interface for attribute categories
interface AttributeCategory {
    category: string;
    attributes: any[];
}

declare global {
    interface Window {
        isAlphaReady: Status;
        isPluginReady: Status;
    }
}
export class PackageInitializerUtil {
    private environment: { [key: string]: string } = {};

    constructor(
        private projectId: string,
        private tenantId: string,
        private projectTagVersion: string,
        private loadElements: boolean = true,
    ) {
        this.environment = window.environments;
    }

    createApiRequestBody(
        endpoint: string,
        environmentKey: 'ALPHA_MODULE_SERVICE_URL' | 'ALPHA_CONFIG_SERVICE_URL',
        requestBody: any = {},
        method: 'POST' | 'GET',
    ) {
        const apiRequest = {
            path: endpoint,
            url: { type: 'environment', value: { __alpha_env__: environmentKey } },
            headers: { 'Content-Type': 'application/json' },
            method,
            body: requestBody,
            apiAuth: {},
        };

        if (this.environment['SKIP_ALPHA_AUTH'] !== 'true') {
            apiRequest.apiAuth = {
                authType: 'client',
                ids_url: { type: 'environment', value: { __alpha_env__: 'IDS_URL' } },
                path: '/token',
                method: 'POST',
                grant_type: { key: 'string', value: 'client_credentials' },
                headers: [{ key: 'Content-Type', value: 'application/x-www-form-urlencoded' }],
                body: [
                    { key: 'client_secret', type: 'environment', value: { __alpha_env__: 'IDS_CLIENT_SECRET' } },
                    { key: 'client_id', type: 'environment', value: { __alpha_env__: 'IDS_CLIENT_ID' } },
                ],
            };
        }
        return apiRequest;
    }

    async initializePackages() {
        await this.loadAlphaModules();
        if (this.loadElements) {
            await this.requestForElements();
        }
        return true;
    }

    async loadAlphaModules(injectPlugins: boolean = true, adminBundlesScriptBasePath?: string) {
        try {
            window.isAlphaReady = Status.PROCESSING;

            const requestBody = this.createApiRequestBody(
                '/bundles?getPlugins=false',
                'ALPHA_MODULE_SERVICE_URL',
                {},
                'GET',
            );

            const response = await fetch(`${this.environment['ALPHA_UI_MAKE_REQUEST_URL']}?${requestBody.path}`, {
                method: 'POST',
                body: JSON.stringify(requestBody),
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Failed to fetch Alpha Modules');
            }

            const alphaModulePlugin = await response.json();
            if (alphaModulePlugin?.main) {
                this.injectScriptFromUrl(alphaModulePlugin.main, adminBundlesScriptBasePath);
            }
        } catch (error: any) {
            window.isAlphaReady = Status.ERROR;
            throw {
                injectPlugins,
                error: `[PackageInitializer][loadAlphaModules] ${error?.message}`,
                stack: error?.stack,
            };
        }
    }

    injectScriptFromUrl(scriptData: { url: string; integrity: string }, adminBundlesScriptBasePath?: string) {
        document.head.querySelector('script[id="alpha_module_main_script"]')?.remove();

        const script = document.createElement('script');
        script.id = 'alpha_module_main_script';

        const timestamp = new Date().getTime();
        let bundleUrl = scriptData.url;
        if (adminBundlesScriptBasePath) {
            const moduleServicePath = bundleUrl.split('/bundles')[1];
            bundleUrl = `${adminBundlesScriptBasePath}${moduleServicePath}`;
        }

        script.src = `${bundleUrl}`;

        script.type = 'module';
        script.integrity = scriptData.integrity;
        script.crossOrigin = 'use-credentials';

        document.head.appendChild(script);
    }

    async loadProjectPackages(packageType: PackageType) {
        window.isPluginReady = Status.PROCESSING;
        const requestBody = this.createApiRequestBody(
            '/project-dependency-config/fetch-all',
            'ALPHA_CONFIG_SERVICE_URL',
            { projectId: this.projectId },
            'POST',
        );

        const response = await fetch(`${this.environment['ALPHA_UI_MAKE_REQUEST_URL']}?${requestBody.path}`, {
            method: 'POST',
            body: JSON.stringify(requestBody),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });

        if (!response.ok) {
            console.error(`Failed to fetch dependencies for package type: ${packageType}`);
            return;
        }

        const result = await response.json();
        if (!result?.length) {
            console.warn('No dependencies found.');
            return;
        }

        const attributes = result.map((pkg: any) => ({
            ...pkg,
            version: pkg.packageVersion,
            versionId: pkg.packageVersionId,
        }));

        return this.processAndInjectPackages(attributes, packageType);
    }

    async processAndInjectPackages(attributes: any, packageType: PackageType): Promise<boolean> {
        if (!attributes?.length) {
            console.warn('No valid attributes found in the response data.');
            return false;
        }

        if ((window as any).isAWSrunning && packageType === PackageType.COMPONENT) {
            const marketPlaceURL = this.environment['ALPHA_UI_MODULE_SERVICE_URL'];
            const packageList = attributes
                .map(async ({ category, value }: { category: string; value: string }, i: number) => {
                    if (category) {
                        const valueJson = { ...JSON.parse(value || '{}') };
                        const { packageName, packageId, versionId } = valueJson;
                        const bundleUrl = `${marketPlaceURL}/marketplace/packages/file/${packageName}/${packageId}/${versionId}`;
                        const iconUrl = `${marketPlaceURL}/marketplace/packages/icon/${packageId}/${versionId}`;
                        return {
                            ...valueJson,
                            metadata: await this.fetchComponentAndExtractMetadata(bundleUrl, iconUrl),
                        };
                    }
                    return null;
                })
                .filter(Boolean);

            const packageListData = await Promise.allSettled(packageList);
            // Dispatch custom event with palette update
            const paletteList = packageListData
                .map((item: any) => {
                    if (item.status === 'fulfilled' && item.value) {
                        return item.value?.metadata;
                    }
                    return null;
                })
                .filter(Boolean);
            window.dispatchEvent(
                new CustomEvent('alpha-ui-builder:palette-update', {
                    detail: {
                        paletteList,
                    },
                }),
            );
            return true;
        } else {
            return this.fetchBundleAndInjectScript(attributes, packageType);
        }
    }

    async fetchComponentAndExtractMetadata(srcUrl: string, fetchIconUrl: string) {
        const module = await import(srcUrl);

        const constructor = Object.values(module)[0];
        return this.getComponentMetadata(constructor, await this.fetchIcon(fetchIconUrl));
    }

    async fetchIcon(fetchIconUrl: string) {
        const response = await fetch(fetchIconUrl);
        const iconResponse = await response.json();
        return iconResponse?.iconSrc || '';
    }

    async fetchBundleAndInjectScript(
        packageList: any[],
        packageType: PackageType,
        bundleType: 'ap' | 'customCode' = 'ap',
    ) {
        try {
            const bundleContent = await this.fetchPackageBundle(packageList, packageType, bundleType);
            this.replaceAndInjectScript(packageType, bundleContent);
        } catch (error: any) {
            window.isPluginReady = Status.ERROR;
            const event = new CustomEvent(`alpha-${bundleType == 'ap' ? 'ap' : 'customCodePlugin'}-loaded`, {
                detail: {
                    success: false,
                    errorKey: error?.code || 'GENERIC_INJECTION_ERROR',
                    message: `[${bundleType == 'ap' ? 'Project Plugin Dependency' : 'Custom Code Plugin Dependency'}] ${error?.message || 'Fail to Inject'}`,
                },
            });
            window.dispatchEvent(event);
        }
        return true;
    }

    async fetchPackageBundle(
        packages: { packageName: string; packageId: string; versionId: string; version: string }[],
        packageType: PackageType,
        bundleType: 'ap' | 'customCode' = 'ap',
    ): Promise<string> {
        if (!packages.length) {
            console.warn(`No Packages Found for ${bundleType == 'ap' ? 'Project' : 'Custom Code'}`);
            const error = new Error(`No Packages Found for ${bundleType == 'ap' ? 'Project' : 'Custom Code'}`);
            (error as any).code = 'NO_PACKAGES_FOUND';
        }
        const requestBody: any = {
            configId: this.projectId,
            configVersion: this.projectTagVersion || '1.0.0',
            packages,
        };

        if (bundleType === 'ap') {
            requestBody['configType'] = 'ap';
        } else if (bundleType === 'customCode') requestBody['configType'] = 'customCodePlugin';

        const endpoint = packageType === PackageType.COMPONENT ? 'components' : 'plugins';

        const apiRequest = this.createApiRequestBody(
            `/marketplace/packages/bundle/${endpoint}/create`,
            'ALPHA_MODULE_SERVICE_URL',
            requestBody,
            'POST',
        );

        const response = await fetch(`${this.environment['ALPHA_UI_MAKE_REQUEST_URL']}?${apiRequest.path}`, {
            method: 'POST',
            body: JSON.stringify(apiRequest),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });

        const responseText = await response.text();

        if (!response.ok) {
            const error = new Error(responseText);
            (error as any).code = 'NO_PACKAGES_FOUND';
            throw error;
        }

        return responseText;
    }

    /**
     * Replace and inject a script based on package type
     * @param {PackageType} packageType Type of package (module or component)
     * @param {string} scriptContent Content of the script to inject
     */
    public replaceAndInjectScript(packageType: PackageType, scriptContent: string): void {
        const scriptId = `${this.projectId}-${this.projectTagVersion}-${packageType}`;

        // Remove existing script with the same ID
        document.querySelector(`script[id="${scriptId}"]`)?.remove();
        this.injectScript(scriptId, scriptContent);
    }

    /**
     * Generic script injection method
     * @param {string} scriptId Unique identifier for the script
     * @param {string} scriptContent Content of the script
     * @param {Function} onLoadCallback Optional callback after script load
     */
    injectScript(scriptId: string, scriptContent: string, onLoadCallback?: () => void): void {
        if (!scriptContent.trim()) {
            console.error('Error: Script content is empty!');
            return;
        }

        const script = document.createElement('script');
        script.type = 'module';
        script.id = scriptId;
        script.textContent = scriptContent + '\n//# sourceURL=dynamic-script.js';
        document.head.appendChild(script);
        onLoadCallback?.();
    }

    /**
     * Get metadata for a component constructor
     * @param constructor Component constructor
     * @returns Component metadata or null
     */
    private getComponentMetadata = (constructor: any, iconPath: string): ComponentMetadata | null => {
        const options = Reflect.getMetadata('AlphaComponent', constructor.prototype);
        if (!options) {
            return null;
        }

        const alphaAttributes = Reflect.getMetadata('AlphaAttribute', constructor.prototype) || [];
        const attributesByCategory: Record<string, any[]> = {};
        const outputs: any[] = [];

        alphaAttributes.forEach((attr: any) => {
            if (attr.type === 'event') {
                outputs.push(attr);
                return;
            }

            attr.category ||= 'Additional Properties';
            if (!Array.isArray(attributesByCategory[attr.category])) {
                attributesByCategory[attr.category] = [];
            }
            attributesByCategory[attr.category].push(attr);
        });

        const categoryLabel = options.label + ' Properties';
        const categoriesPriority = [
            'Label Properties',
            categoryLabel,
            'Options',
            'Binding Variable',
            'Validations',
            'Additional Properties',
        ];

        let inputs = Object.keys(attributesByCategory).map((key) => ({
            category: key,
            attributes: attributesByCategory[key]?.reverse(),
        }));

        let orderedInputs: AttributeCategory[] = [];
        categoriesPriority.forEach((category) => {
            const matchingItem = inputs.find((item) => item.category.toLowerCase() === category.toLowerCase());
            if (matchingItem) {
                orderedInputs.push(matchingItem);
            }
        });

        const nonCategorizedInputs = inputs.filter(
            (item) => !categoriesPriority.some((category) => category.toLowerCase() === item.category.toLowerCase()),
        );
        orderedInputs = [...orderedInputs, ...nonCategorizedInputs];

        const dependencyOptions = Array.isArray(options.dependencyOptions) ? options.dependencyOptions : [];

        return {
            category: options.category,
            label: options.label,
            name: options.selector,
            iconPath,
            version: options.componentVersion,
            config: {
                paletteCategory: options.category,
                inputs: orderedInputs,
                outputs,
                dependencyOptions,
            },
        };
    };

    private createScriptContent = (packages: string[] = [], alphaModuleServerUrl: string) => {
        return /**js */ `
        const getMetadata = (obj) => {
          const c = Object.values(obj)[0];
          const options = Reflect.getMetadata('AlphaComponent', c.prototype);
          if(!options) {
            return null;
          }
    
          const alphaAttributes = Reflect.getMetadata("AlphaAttribute", c.prototype) || [];
          const attrsObj = {};
          const outputs = [];
          alphaAttributes?.forEach((attr)=>{
            if(attr.type === 'event'){
                outputs.push(attr)
                return;
            }
            attr.category ||= "Additional Properties";
            if(!Array.isArray(attrsObj[attr.category])){
                attrsObj[attr.category] = [];
            }
            attrsObj[attr.category].push(attr);
          })
          const category = options.label +' '+ 'Properties';
          const list = ['Label Properties', category, 'Options', 'Binding Variable', 'Validations', 'Additional Properties']
          let inputs = Object.keys(attrsObj || {}).map((key)=>({ category:key, attributes:attrsObj[key]?.reverse() }));
          let newInput =[];
          list.forEach(category => {
            const matchingItem = inputs.find(item => item.category.toLowerCase() === category.toLowerCase());
            if (matchingItem) {
                newInput.push(matchingItem);
            }
          });
    
          const nonCategorized = inputs.filter(item => !list.some(category => category.toLowerCase() === item.category.toLowerCase()));
          inputs = [...newInput,...nonCategorized];
          const iconPath = "${alphaModuleServerUrl}/elements/"+options.componentName+"/asset/"+options.icon;
          const dependencyOptions = Array.isArray(options.dependencyOptions)? options.dependencyOptions : [];
          return {
                  category: options.category,
                  label: options.label,
                  name: options.selector,
                  iconPath,
                  version: options.componentVersion,
                  config: { paletteCategory: options.category, inputs, outputs, dependencyOptions }
              }
          }
          const imports = [${(Array.isArray(packages) ? packages : []).map((imp: { src: string } | string) => `'${typeof imp === 'object' ? imp.src : imp}'`).join(', ')}];
          const elements = await Promise.allSettled(imports.map((imp) => import(imp)));
          const packages = ${JSON.stringify(packages)}
          const paletteList = elements.map((e,i) => {
            if(e.status !== 'fulfilled') {
                console.warn(e.reason);
                return;
            };
            const metadata = getMetadata(e.value);
            if(typeof packages[i] === 'object'){
                if (metadata) {
                    metadata.iconPath = packages[i]?.icon || '';
                }
            }
            return metadata
          }).filter(Boolean) || [];
          window.dispatchEvent(new CustomEvent("alpha-ui-builder:palette-update", {
            detail: {
              paletteList,
            },
          }));
        `;
    };
    private getModuleServerApiBody = (path: string) => {
        const apiOptions: any = {
            body: {},
            path: path,
            url: {
                type: 'environment',
                value: {
                    __alpha_env__: 'ALPHA_MODULE_SERVICE_URL',
                },
            },
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'get',
        };
        if (this.environment['SKIP_ALPHA_AUTH'] !== 'true') {
            apiOptions.apiAuth = {
                authType: 'client',
                ids_url: {
                    type: 'environment',
                    value: {
                        __alpha_env__: 'IDS_URL',
                    },
                },
                path: '/token',
                method: 'post',
                grant_type: {
                    key: 'string',
                    value: 'client_credentials',
                },
                headers: [
                    {
                        key: 'Content-Type',
                        value: 'application/x-www-form-urlencoded',
                    },
                ],
                body: [
                    {
                        key: 'client_secret',
                        type: 'environment',
                        value: {
                            __alpha_env__: 'IDS_CLIENT_SECRET',
                        },
                    },
                    {
                        key: 'client_id',
                        type: 'environment',
                        value: {
                            __alpha_env__: 'IDS_CLIENT_ID',
                        },
                    },
                ],
            };
        }
        return apiOptions;
    };
    async requestForElements() {
        const alphaModuleServerUrl = this.environment['ALPHA_UI_MODULE_SERVICE_URL'];
        if (!alphaModuleServerUrl) {
            console.error('alpha-modules-server url not found');
            return;
        }
        const script = document.createElement('script');
        try {
            const elementsReqBody = this.getModuleServerApiBody('/elements');
            const scriptUrls = await fetch(`${this.environment['ALPHA_UI_MAKE_REQUEST_URL']}?${elementsReqBody.path}`, {
                method: 'post',
                body: JSON.stringify(elementsReqBody),
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            }).then((res) => res.json());
            script.innerHTML = this.createScriptContent(scriptUrls, alphaModuleServerUrl);
            script.type = 'module';
            script.crossOrigin = 'use-credentials';
            document.head.appendChild(script);
        } catch (error) {
            console.error('Error fetching Elements:', error);
        }
    }

    requestForBundles = async (adminBundlesScriptBasePath: string = '') => {
        const bundlesReqBody = this.getModuleServerApiBody('/bundles?getPlugins=true');
        const alphaPlugins = await fetch(`${this.environment['ALPHA_UI_MAKE_REQUEST_URL']}?${bundlesReqBody.path}`, {
            method: 'post',
            body: JSON.stringify(bundlesReqBody),
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        })
            .then((res) => res.json())
            .catch((e) => console.error(e));

        if (alphaPlugins?.main) {
            window.addEventListener('alpha_ready', () => {
                alphaPlugins?.plugins.forEach((bundle: { url: string; integrity: string }) => {
                    this.registerToScript(bundle, adminBundlesScriptBasePath);
                });
            });
            this.registerToScript(alphaPlugins.main, adminBundlesScriptBasePath);
        }
    };

    registerToScript = (bundle: { url: string; integrity: string }, adminBundlesScriptBasePath: string = '') => {
        const script = document.createElement('script');
        let bundleUrl = bundle.url;
        if (adminBundlesScriptBasePath) {
            const moduleServicePath = bundleUrl.split('/bundles')[1];
            bundleUrl = `${adminBundlesScriptBasePath}${moduleServicePath}`;
        }
        script.setAttribute('src', bundleUrl);
        script.setAttribute('type', 'module');
        script.setAttribute('integrity', bundle.integrity);
        script.setAttribute('crossorigin', 'use-credentials');
        document.head.append(script);
    };
}
